// import react hooks
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

// import mantine UI components
import {
  Paper,
  Group,
  Button,
  LoadingOverlay,
  Alert,
  Title,
  Box,
} from '@mantine/core';
import { IconPlus, IconInfoCircle } from '@tabler/icons-react';

// import custom hooks
import usePartner from '../hooks/partners/usePartner.ts';
import useParkingContract from '../hooks/parking-contracts/use-parking-contract.ts';
import useCreateParkingContract from '../hooks/parking-contracts/use-create-parking-contract.ts';

// import interface, type
import type {
  BaseCreateParkingContractRequest,
  CreateDefaultParkingContractRequest,
  CreateSpecifyParkingContractRequest,
} from '../api/requests/parking-contract-request.ts';
import type {
  MutateParkingContractFormValues,
  MutateParkingContractFormInitialValues,
} from '../components/ui/MutateParkingContractForm.tsx';
import type {
  DefaultConfigType,
  BankDiscountConfigType,
  PbaReferralConfigType,
} from '../models/parking-contract.ts';

// import custom UI component
import MutateParkingContractForm from '../components/ui/MutateParkingContractForm.tsx';
import PreviousPageButton from '../components/ui/PreviousPageButton.tsx';

export default function ParkingContractNewPage() {
  // react hooks
  const navigate = useNavigate();
  const { state } = useLocation();
  const { partnerId } = useParams();
  const isCloned: boolean = !!state?.cloneId;
  const cloneId: string = state?.cloneId;

  // api hooks
  const { partner, isLoading: isPartnerLoading } = usePartner(partnerId);
  const {
    parkingContract: cloneContract,
    isLoading: isClonedParkingContractLoading,
  } = useParkingContract(partnerId, cloneId, isCloned);
  const {
    mutate: createParkingContract,
    isPending: isCreateParkingContractPending,
  } = useCreateParkingContract();

  // UI hooks
  const [isClonedNoticeClosed, setClonedNoticeClosed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isPartnerLoading || isClonedParkingContractLoading) {
    return (
      <LoadingOverlay visible loaderProps={{ color: 'cyan', type: 'bars' }} />
    );
  }

  // constants
  const shouldDisplayClonedNotice: boolean = isCloned && !isClonedNoticeClosed;
  const initialFormValues = (): MutateParkingContractFormInitialValues => {
    let initialValues: MutateParkingContractFormInitialValues;

    if (cloneContract) {
      const alprProfitSharingConfig = cloneContract.profitSharingConfigs.ALPR;
      const kioskProfitSharingConfig = cloneContract.profitSharingConfigs.KIOSK;
      const payTaipeiProfitSharingConfig =
        cloneContract.profitSharingConfigs.PAY_TAIPEI;
      initialValues = {
        name: '',
        profileType: cloneContract.profileType,
        paperContractStartAt: null,
        paperContractEndAt: null,
        activeStartAt: null,
        activeEndAt: null,
        disbursementFeeProcessingMethod:
          cloneContract.disbursementFeeProcessingMethod,
        refundFeeProcessingMethod: cloneContract.refundFeeProcessingMethod,
        billingCycle: cloneContract.billingCycle,
        parkingLotFunctionTypes: cloneContract.parkingLotFunctionTypes,
        profitSharingConfigs: {
          ALPR: {
            parkingLotFunctionType:
              alprProfitSharingConfig.parkingLotFunctionType,
            allowDisbursementAmountOverLimit:
              alprProfitSharingConfig.allowDisbursementAmountOverLimit,
            defaultConfig: {
              type: alprProfitSharingConfig.defaultConfig.type,
              value: alprProfitSharingConfig.defaultConfig.value,
            },
            bankDiscountConfig: {
              type: alprProfitSharingConfig.bankDiscountConfig.type,
              value: alprProfitSharingConfig.bankDiscountConfig.value,
            },
            pbaReferralConfig: {
              type: alprProfitSharingConfig.pbaReferralConfig.type,
              value: alprProfitSharingConfig.pbaReferralConfig.value,
            },
          },
          KIOSK: {
            parkingLotFunctionType:
              kioskProfitSharingConfig.parkingLotFunctionType,
            allowDisbursementAmountOverLimit:
              kioskProfitSharingConfig.allowDisbursementAmountOverLimit,
            defaultConfig: {
              type: kioskProfitSharingConfig.defaultConfig.type,
              value: kioskProfitSharingConfig.defaultConfig.value,
            },
            bankDiscountConfig: {
              type: kioskProfitSharingConfig.bankDiscountConfig.type,
              value: kioskProfitSharingConfig.bankDiscountConfig.value,
            },
            pbaReferralConfig: {
              type: kioskProfitSharingConfig.pbaReferralConfig.type,
              value: kioskProfitSharingConfig.pbaReferralConfig.value,
            },
          },
          PAY_TAIPEI: {
            parkingLotFunctionType:
              payTaipeiProfitSharingConfig.parkingLotFunctionType,
            allowDisbursementAmountOverLimit:
              payTaipeiProfitSharingConfig.allowDisbursementAmountOverLimit,
            defaultConfig: {
              type: payTaipeiProfitSharingConfig.defaultConfig.type,
              value: payTaipeiProfitSharingConfig.defaultConfig.value,
            },
            bankDiscountConfig: {
              type: payTaipeiProfitSharingConfig.bankDiscountConfig.type,
              value: payTaipeiProfitSharingConfig.bankDiscountConfig.value,
            },
            pbaReferralConfig: {
              type: payTaipeiProfitSharingConfig.pbaReferralConfig.type,
              value: payTaipeiProfitSharingConfig.pbaReferralConfig.value,
            },
          },
        },
        existingAttachments: [],
      };
    } else {
      const emptyProfitSharingConfig = {
        defaultConfig: {
          type: 'FIXED_FEE' as DefaultConfigType,
          value: '0',
        },
        bankDiscountConfig: {
          type: 'NON' as BankDiscountConfigType,
          value: null,
        },
        pbaReferralConfig: {
          type: 'NON' as PbaReferralConfigType,
          value: null,
        },
      };
      initialValues = {
        name: '',
        profileType: 'DEFAULT',
        paperContractStartAt: null,
        paperContractEndAt: null,
        activeStartAt: null,
        activeEndAt: null,
        disbursementFeeProcessingMethod: 'NO_DEDUCTION',
        refundFeeProcessingMethod: 'REFUND_ALL',
        billingCycle: 'MONTHLY',
        parkingLotFunctionTypes: ['ALPR'],
        profitSharingConfigs: {
          ALPR: {
            parkingLotFunctionType: 'ALPR',
            allowDisbursementAmountOverLimit: false,
            ...emptyProfitSharingConfig,
          },
          KIOSK: {
            parkingLotFunctionType: 'KIOSK',
            allowDisbursementAmountOverLimit: false,
            ...emptyProfitSharingConfig,
          },
          PAY_TAIPEI: {
            parkingLotFunctionType: 'PAY_TAIPEI',
            allowDisbursementAmountOverLimit: false,
            ...emptyProfitSharingConfig,
          },
        },
        existingAttachments: [],
      };
    }

    return initialValues;
  };

  // callback functions
  function onSubmit(values: MutateParkingContractFormValues): void {
    if (isCreateParkingContractPending) return;

    /* ===== 建構請求中共用部分 ===== */
    const baseRequest: BaseCreateParkingContractRequest = {
      partnerId: partnerId,
      name: values.name,
      paperContractStartAt: values.paperContractStartAt,
      paperContractEndAt: values.paperContractEndAt,
      disbursementFeeProcessingMethod: values.disbursementFeeProcessingMethod,
      refundFeeProcessingMethod: values.refundFeeProcessingMethod,
      billingCycle: values.billingCycle,

      /* ===== 各交易場景 Profit Sharing 配置 ===== */
      ...{
        alprProfitSharingEnabled:
          values.parkingLotFunctionTypes.includes('ALPR'),
        alprAllowDisbursementAmountOverLimit:
          values.profitSharingConfigs.ALPR.allowDisbursementAmountOverLimit,
        alprDefaultProfitSharingType:
          values.profitSharingConfigs.ALPR.defaultConfig.type,
        alprDefaultProfitSharingValue:
          values.profitSharingConfigs.ALPR.defaultConfig.value,
        alprBankProfitSharingType:
          values.profitSharingConfigs.ALPR.bankDiscountConfig.type,
        alprBankProfitSharingValue:
          values.profitSharingConfigs.ALPR.bankDiscountConfig.value,
        alprPbaProfitSharingType:
          values.profitSharingConfigs.ALPR.pbaReferralConfig.type,
        alprPbaProfitSharingValue:
          values.profitSharingConfigs.ALPR.pbaReferralConfig.value,
      },
      ...{
        kioskProfitSharingEnabled:
          values.parkingLotFunctionTypes.includes('KIOSK'),
        kioskAllowDisbursementAmountOverLimit:
          values.profitSharingConfigs.KIOSK.allowDisbursementAmountOverLimit,
        kioskDefaultProfitSharingType:
          values.profitSharingConfigs.KIOSK.defaultConfig.type,
        kioskDefaultProfitSharingValue:
          values.profitSharingConfigs.KIOSK.defaultConfig.value,
        kioskBankProfitSharingType:
          values.profitSharingConfigs.KIOSK.bankDiscountConfig.type,
        kioskBankProfitSharingValue:
          values.profitSharingConfigs.KIOSK.bankDiscountConfig.value,
        kioskPbaProfitSharingType:
          values.profitSharingConfigs.KIOSK.pbaReferralConfig.type,
        kioskPbaProfitSharingValue:
          values.profitSharingConfigs.KIOSK.pbaReferralConfig.value,
      },
      ...{
        payTaipeiProfitSharingEnabled:
          values.parkingLotFunctionTypes.includes('PAY_TAIPEI'),
        payTaipeiAllowDisbursementAmountOverLimit:
          values.profitSharingConfigs.PAY_TAIPEI
            .allowDisbursementAmountOverLimit,
        payTaipeiDefaultProfitSharingType:
          values.profitSharingConfigs.PAY_TAIPEI.defaultConfig.type,
        payTaipeiDefaultProfitSharingValue:
          values.profitSharingConfigs.PAY_TAIPEI.defaultConfig.value,
        payTaipeiBankProfitSharingType:
          values.profitSharingConfigs.PAY_TAIPEI.bankDiscountConfig.type,
        payTaipeiBankProfitSharingValue:
          values.profitSharingConfigs.PAY_TAIPEI.bankDiscountConfig.value,
        payTaipeiPbaProfitSharingType:
          values.profitSharingConfigs.PAY_TAIPEI.pbaReferralConfig.type,
        payTaipeiPbaProfitSharingValue:
          values.profitSharingConfigs.PAY_TAIPEI.pbaReferralConfig.value,
      },

      /* ===== 合約附件 ===== */
      file: values.newAttachments[0].file,
    };

    /* ===== 依據合約類型決定建構策略 ===== */
    const request:
      | CreateDefaultParkingContractRequest
      | CreateSpecifyParkingContractRequest =
      values.profileType === 'DEFAULT'
        ? ({
            ...baseRequest,
            profileType: 'DEFAULT',
            activeStartAt: values.activeStartAt,
            activeEndAt: values.activeEndAt,
          } as CreateDefaultParkingContractRequest)
        : ({
            ...baseRequest,
            profileType: 'SPECIFY',
          } as CreateSpecifyParkingContractRequest);

    createParkingContract(request, {
      onSuccess: (id) => {
        // 轉導至 editPage
        navigate(`/partners/${partnerId}/parking-contracts/${id}/edit`, {
          state: { created: true },
        });
      },
    });
  }

  return (
    <>
      {/* Main Content */}
      <PreviousPageButton
        href={`/partners/${partnerId}`}
        content={'返回業者詳情頁'}
      />
      {shouldDisplayClonedNotice && (
        <Alert
          variant="light"
          color="green"
          mb={20}
          icon={<IconInfoCircle />}
          withCloseButton
          onClose={() => setClonedNoticeClosed(true)}
          title={`成功複製「${cloneContract.name}」合約`}
        >
          僅複製「合約套用方式」、「手續費扣除方式」、「退款手續費處理方式」、「出帳週期」、「交易情境」、「分潤設定」等欄位，其餘欄位仍需手動填寫
        </Alert>
      )}
      <Paper radius="lg" p="xl" shadow="xs" mb={70}>
        <Title order={3}>{`${partner.name} | 停車場合約`}</Title>
        <MutateParkingContractForm
          initialValues={initialFormValues()}
          onSubmit={onSubmit}
        />
      </Paper>

      {/* Footer */}
      <Box
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        h={70}
        p="md"
        bg="var(--mantine-color-white)"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          zIndex: 100,
        }}
      >
        <Group justify="flex-end">
          <Button
            type="submit"
            form="mutateParkingContractForm"
            loading={isCreateParkingContractPending}
            fw={400}
            leftSection={<IconPlus />}
          >
            新增
          </Button>
        </Group>
      </Box>
    </>
  );
}
